<template>
    <div class="mb-4">
        <h5>{{ $t('Company Information') }}</h5>
        <div class="px-0 mb-3 mt-3">
            <b-row class="mb-4">
                <b-col cols="12" md="3">
                    <label>{{ $t('Business license photo') }} <span class="text-danger">*</span></label>
                    <span class="position-relative">
                        <button type="button" class="flat-button position-absolute" @click="togglePopup()">
                            <img :src="`${publicPath}/img/rcp/icon/btn-info.png`" class="align-top">
                        </button>
                        <NewPopupInfo  :name="'license'" v-if="showPopup" :styleCustom="{ left: '95px' }">
                        </NewPopupInfo>
                    </span>
                </b-col>

                <b-col cols="12" md="9">
                    <FileUploader :type="'browse'" :ref="'license'" :allowed_types="['jpg', 'jpeg', 'png']"
                        :size="4 * 1024 * 1024" @capture-file="captureFile($event)" @remove-image="removeImage($event)">
                    </FileUploader>
                    <div v-if="error['license']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['license']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Business License Number') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.licenseNo"
                        @keydown="onChange('licenseNo')" />
                    <div v-if="error['licenseNo']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['licenseNo']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Chinese Business Name') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.name" @keydown="onChange('name')" />
                    <div v-if="error['name']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['name']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('English Business Name') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.enName" @keydown="onChange('enName')" />
                    <div v-if="error['enName']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['enName']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Period of Business') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <date-picker v-model="params.endDate" :lang="lang_date" input-class="form-control"
                        format="YYYY-MM-DD" value-type="YYYY-MM-DD" @change="onChange('endDate')"></date-picker>
                    <div v-if="error['endDate']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['endDate']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Type of Company') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.companyType"
                        @keydown="onChange('companyType')" />
                    <div v-if="error['companyType']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['companyType']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col cols="12" md="3">
                    <label>{{ $t('Scope of Business') }} <span class="text-danger">*</span></label>
                </b-col>

                <b-col sm="12" md="9">
                    <input type="text" class="form-control" v-model="params.managementScope"
                        @keydown="onChange('managementScope')" />
                    <div v-if="error['managementScope']" class="invalid-feedback d-block mb-3">
                        {{ $t(error['managementScope']) }}
                    </div>
                </b-col>
            </b-row>

            <b-row class="mb-3">
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('Province') }} <span class="text-danger">*</span></label>
                        <Select2 v-model="params.province" :options="provinceOpt" @select="onSelectProvince($event)"
                            :settings='{ escapeMarkup: doEscapeMarkup, placeholder: $t("Province") }'
                            class="form-control" />
                        <div v-if="error['province']" class="invalid-feedback d-block mb-3">
                            {{ $t(error['province']) }}
                        </div>
                    </div>
                </b-col>
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('City') }} <span class="text-danger">*</span></label>
                        <Select2 v-model="params.city" :options="cityOpt" @select="onSelectCity($event)"
                            :settings='{ escapeMarkup: doEscapeMarkup, placeholder: $t("City") }'
                            class="form-control" />
                        <div v-if="error['city']" class="invalid-feedback d-block mb-3">
                            {{ $t(error['city']) }}
                        </div>
                    </div>
                </b-col>
                <b-col xl="4" sm="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('District') }}</label>
                        <Select2 v-model="params.area" :options="districtOpt" @select="onSelectDistrict($event)"
                            :settings='{ escapeMarkup: doEscapeMarkup, placeholder: $t("District") }'
                            class="form-control" />
                        <div v-if="error['district']" class="invalid-feedback d-block mb-3">
                            {{ $t(error['district']) }}
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row>
                <b-col xl="12">
                    <div class="form-group mb-0">
                        <label>{{ $t('Address') }} <span class="text-danger">*</span></label>
                        <input type="text" name="addresss" v-model="params.address" class="form-control"
                            @keydown="addressCondition($event)" @keyup="onchangeAddress($event)" />
                        <div v-if="error['address']" class="invalid-feedback d-block mb-3">
                            {{ $t(error['address']) }}
                        </div>
                    </div>
                </b-col>
            </b-row>
        </div>

    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import Select2 from 'v-select2-component';

import NewDocOption from "@/components/Kyc/NewDocOption";
import FileUploader from '@/components/Kyc/FileUploader.vue';
import NewPopupInfo from '@/components/NewPopupInfo.vue';
import CreateAccount from '@/services/CreateAccount';

import 'vue2-datepicker/locale/zh-cn';
import 'vue2-datepicker/locale/zh-tw';
import 'vue2-datepicker/locale/ja';
import 'vue2-datepicker/locale/en';

export default {
    data() {
        return {
            publicPath: process.env.BASE_URL != '/' ? process.env.BASE_URL : '',
            maxSize: 4,
            cityOpt: [],
            districtOpt: [],
            provinceOpt: [],
            showPopup: false,
            lang_date: 'en',
            success: null,
            userNo: null,
            accountId: null,
            file: {
                license: ''
            },
            params: {
                licenseNo: '',
                license: '',
                name: '',
                enName: '',
                city: '',
                province: '',
                area: '',
                address: '',
                endDate: '',
                companyType: '',
                managementScope: ''
            },
            error: {
                licenseNo: '',
                license: '',
                name: '',
                enName: '',
                city: '',
                province: '',
                district: '',
                address: '',
                endDate: '',
                companyType: '',
                managementScope: ''
            },
            optional: ['district']
        }
    },
    components: {
        NewDocOption,
        FileUploader,
        Select2,
        NewPopupInfo,
        DatePicker
    },
    props: ['information', 'user_no', 'account_id'],
    methods: {
        getFile(index, file_id) {
            let self = this;
            CreateAccount.getFile({ fileId: file_id }).then(response => {
                let data_file = response.data.data[0];
                if (data_file.type == 7) {
                    self.params.license = data_file.file;
                    self.$refs['license'].image = data_file.file;
                }
            });
        },
        addressCondition($event) {
            if ($event.keyCode == 13) {
                $event.preventDefault();
            }
            this.onChange('address');
        },
        onchangeAddress($event) {
            let msg = $event.target.value.replace("\n", " ");
            $event.target.value = msg;
        },
        validate() {
            let success = true;
            let self = this;
            Object.keys(this.params).forEach(function (key) {
                if (!self.params[key] && !self.optional.includes(key)) {
                    self.error[key] = "can not be blank.";
                    success = false;
                }
            });

            return success;
        },
        uploadFile(account_id, user_no) {
            let self = this;
            this.success = null;
            this.accountId = account_id;
            this.userNo = user_no;
            this.error.license = '';

            CreateAccount.uploadFile({
                "accountId": self.accountId,
                "userNo": "" + self.userNo,
                "fileUpload": [
                    {
                        "type": 7,
                        "maxSize": self.maxSize * 1024 * 1024,
                        "file": self.params.license
                    }
                ]
            }).then(response => {
                const data = response.data.data;
                let fileList = data["file"];
                for (let i = 0; i < fileList.length; i++) {
                    if (fileList[i].type == 7) {
                        // self.params.license = fileList[i].file;
                        self.file.license = fileList[i].fileId;
                    }
                }
                self.success = true;
                self.$emit('file-uploaded');
            }).catch(err => {
                self.checkError(err);
            });
        },
        checkError(err) {
            try {
                let self = this;
                const response = err.response;
                if (response.status == 400) {
                    if (typeof response.data.message === 'string') {
                        this.$notify({
                            type: 'warn',
                            text: response.data.message
                        });
                    }
                    else {
                        if (response.data.message['file']) {
                            let fileList = response.data.message['file'];
                            for (let i = 0; i < fileList.length; i++) {
                                if (fileList[i].type == 7) {
                                    self.error.license = fileList[i].message;
                                }
                            }
                        }
                        self.success = false;
                        self.$emit('file-uploaded');
                    }
                }
                else if (response.status == 401 && response.data.name == "Unauthorized") {
                    localStorage.removeItem("token");
                    localStorage.removeItem("member");
                    localStorage.removeItem("isFirstTime");
                    this.$router.push({
                        path: '/login',
                    });
                }
                else {
                    if (localStorage.getItem("token")) {
                        var msg = (response.data) ? response.data.message : err.message;
                        this.$notify({
                            type: 'warn',
                            text: msg
                        })
                    }
                }
            } catch (e) {
                console.log(err);
            }
        },
        onSelectDistrict({ id, text }) {
            var self = this;
            this.onChange('district');
        },
        onSelectCity({ id, text, code }) {
            var self = this;
            this.onChange('city');
            console.log("=== test ===");
            console.log(code);
            if (text) {
                this.district = "";
            }
            this.getDistrict(code);
        },
        onSelectProvince({ id, text, code }) {
            var self = this;
            this.onChange('province');
            self.districtOpt = [];
            if (text) {
                this.city = "";
                this.district = "";
            }
            this.getCity(code);
        },
        getDistrict(code) {
            console.log(code);
            this.districtOpt = [];
            CreateAccount.getDistrict({ "city_id": code }).then(response => {
                const data = response.data.data;
                data.forEach(item => {
                    this.districtOpt.push({ "id": item.value, "text": item.text, "code": item.code });
                });
            }).catch(err => {
                this.checkError(err);
            });
        },
        getCity(code) {
            this.cityOpt = [];
            CreateAccount.getCity({ "province_id": code }).then(response => {
                const data = response.data.data;
                data.forEach(item => {
                    this.cityOpt.push({ "id": item.value, "text": item.text, "code": item.code });
                });
            }).catch(err => {
                this.checkError(err);
            });
        },
        onChange(name) {
            this.error[name] = "";
        },
        togglePopup() {
            this.showPopup = !this.showPopup;
        },
        selectedDocumentBeneficiary(data) {
            this.doc_type = data.index;
        },
        captureFile($event) {
            this.error.license = "";
            this.params.license = $event;
        },
        removeImage($event) {
            this.params.license = "";
            this.error.license = "";
        },
        doEscapeMarkup(markup) {
            return markup;
        }
    },
    mounted() {
        let self = this;
        this.$nextTick(() => {

            CreateAccount.getProvince({ "country": "CHN" }).then(response => {
                const data = response.data.data;
                this.provinceOpt = [];
                data.forEach(item => {
                    this.provinceOpt.push({ "id": item.value, "text": item.text, "code": item.id });
                });

                if (this.information) {

                    Object.keys(this.params).forEach(function (key) {
                        if (typeof self.file[key] !== "undefined" && self.information[key]) {
                            self.file[key] = self.information[key];
                            self.getFile(key, self.information[key]);
                        }
                        else {
                            self.params[key] = self.information[key];
                        }
                    });

                    if (this.params.province) {
                        let data_province = this.params.province.split("_");
                        this.getCity(data_province[0]);
                    }

                    if (this.params.city) {
                        let data_city = this.params.city.split("_");
                        this.getDistrict(data_city[0]);
                    }
                }

                if (this.user_no) {
                    this.userNo = this.user_no;
                    this.accountId = this.account_id;
                }

            }).catch(err => {
                this.checkError(err);
            });
        });
    }
}
</script>